import { createFileRoute, Link } from "@tanstack/react-router";
import { VENUE_LIST } from "@/data/venues";
import { useReveal } from "@/hooks/use-reveal";
import { PageHero } from "@/components/page-hero";
import heroImg from "@/assets/dj-mariage-reception.jpg";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
const BASE = "https://dj-val.fr";

export const Route = createFileRoute("/lieux/")({
  head: () => ({
    meta: [
      {
        title: "Lieux de Réception Mariage Provence & Luberon | DJ Val",
      },
      {
        name: "description",
        content:
          metaDesc("Les domaines, mas et châteaux de Provence et du Luberon où DJ Val intervient : anecdotes, expertise terrain et conseils acoustiques pour chaque lieu.")
      },
      { property: "og:title", content: "Lieux de Réception Mariage Provence & Luberon | DJ Val" },
      {
        property: "og:description",
        content: metaDesc("Domaines, mas et châteaux où DJ Val anime vos mariages. Expertise terrain et anecdotes.")
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://dj-val.fr/lieux" },
    ],
    links: [{ rel: "canonical", href: "https://dj-val.fr/lieux" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Lieux de réception Provence — DJ Val",
          url: `${BASE}/lieux`,
          hasPart: VENUE_LIST.map((v) => ({
            "@type": "Place",
            name: v.name,
            url: `${BASE}/lieux/${v.slug}`,
            address: {
              "@type": "PostalAddress",
              addressLocality: v.city,
              addressRegion: v.region,
              addressCountry: "FR",
            },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: BASE },
            { "@type": "ListItem", position: 2, name: "Lieux de réception", item: `${BASE}/lieux` },
          ],
        }),
      },
    ],
  }),
  component: VenuesIndex,
});

function VenuesIndex() {
  useReveal();
  return (
    <div className="bg-background text-muted-foreground">
      <PageHero
        image={heroImg}
        eyebrow="Lieux de réception"
        title={<>Les <span className="italic">domaines</span><span className="block">que je connais par cœur</span></>}
        subtitle="Provence, Luberon, Vaucluse : expertise terrain, conseils acoustiques et anecdotes vécues pour chaque domaine, mas et château où j'interviens."
        primary={{ label: "Demander un devis", to: "/contactez-nous" }}
      />
      <ReviewsProof />

      {/* GRID DES LIEUX */}

      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {VENUE_LIST.map((v, i) => (
            <Link
              key={v.slug}
              to="/lieux/$slug"
              params={{ slug: v.slug }}
              className="group sr block border border-[var(--gold-line)] bg-card overflow-hidden hover:border-[var(--gold)] transition-colors"
              data-reveal
              style={{ transitionDelay: `${(i % 3) * 80}ms` }}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={v.hero}
                  alt={v.heroAlt}
                  loading="lazy"
                  decoding="async"
                  width={800}
                  height={600}
                  className="h-full w-full object-cover transition-transform duration-[1500ms] group-hover:scale-110"
                />
              </div>
              <div className="p-6">
                <p className="eyebrow text-[var(--gold)] text-[10px]">
                  {v.city} · {v.region}
                </p>
                <h2 className="font-serif-display text-2xl text-foreground mt-3 group-hover:text-[var(--gold)] transition-colors">
                  {v.name}
                </h2>
                <span className="gold-rule !w-8 my-3" />
                <p className="text-sm text-muted-foreground leading-relaxed font-light line-clamp-3">
                  {v.intro}
                </p>
                {v.prestationsRealisees && (
                  <p className="mt-4 text-[10px] uppercase tracking-[0.2em] text-[var(--gold)]">
                    ✦ {v.prestationsRealisees} prestations réalisées
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* TON LIEU N'EST PAS LÀ ? */}
      <section className="py-20 px-6 border-y border-[var(--gold-line)] bg-card">
        <div className="max-w-3xl mx-auto text-center sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Votre lieu n'est pas listé ?</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
            J'interviens dans <span className="italic">toute la Provence</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 my-8" />
          <p className="text-sm text-muted-foreground font-light leading-relaxed">
            Cette page liste les domaines où je suis intervenu le plus souvent — mais ma zone
            d'action couvre tout le Vaucluse, les Bouches-du-Rhône et le Gard. Que votre lieu soit
            connu ou plus confidentiel, je viens systématiquement le repérer en amont pour adapter
            ma prestation.
          </p>
          <Link
            to="/contactez-nous"
            className="mt-8 inline-block bg-[var(--gold)] text-[var(--ink)] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
          >
            Parlez-moi de votre lieu
          </Link>
        </div>
      </section>
    </div>
  );
}
